import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { adminAPI, api } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2, Users, GraduationCap, Video, ArrowUp, ArrowDown, Search } from 'lucide-react';

const AdminTeamsAndTrainings = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('teams');
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-neutral-border px-6 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-neutral-100" data-testid="back-to-admin"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="text-xl font-serif font-bold text-primary-900">Teams &amp; Training Library</h1>
        </div>
      </header>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        <div className="flex gap-2">
          <Button variant={tab === 'teams' ? 'default' : 'outline'} onClick={() => setTab('teams')} data-testid="tab-teams"><Users className="h-4 w-4 mr-2" />Teams</Button>
          <Button variant={tab === 'trainings' ? 'default' : 'outline'} onClick={() => setTab('trainings')} data-testid="tab-trainings"><GraduationCap className="h-4 w-4 mr-2" />Training Library</Button>
        </div>
        {tab === 'teams' ? <TeamsPanel /> : <TrainingsPanel />}
      </div>
    </div>
  );
};

// =================== TEAMS ===================
const TeamsPanel = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', whatsapp_link: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [members, setMembers] = useState([]);
  const [allMarketers, setAllMarketers] = useState([]);
  const [assignQuery, setAssignQuery] = useState('');

  const load = async () => {
    try { const r = await adminAPI.getTeams(); setTeams(r.data.teams || []); }
    catch { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit({ id: null }); setForm({ name: '', description: '', whatsapp_link: '', status: 'active' }); };
  const openEdit = (t) => { setEdit(t); setForm({ name: t.name, description: t.description || '', whatsapp_link: t.whatsapp_link || '', status: t.status || 'active' }); };
  const save = async () => {
    if (!form.name.trim()) { toast.error('Team name is required'); return; }
    setSaving(true);
    try {
      if (edit?.id) await adminAPI.updateTeam(edit.id, form);
      else await adminAPI.createTeam(form);
      toast.success(edit?.id ? 'Team updated' : 'Team created');
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const performDelete = async () => {
    try { const r = await adminAPI.deleteTeam(confirmDel.id); toast.success(r.data?.message || 'Team deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setConfirmDel(null); }
  };
  const openMembers = async (t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id);
    try {
      const [tm, all] = await Promise.all([
        adminAPI.getTeamMembers(t.id),
        adminAPI.getMarketers({ limit: 5000 }),
      ]);
      setMembers(tm.data.members || []);
      setAllMarketers(all.data.marketers || all.data || []);
    } catch { toast.error('Failed to load members'); }
  };
  const assign = async (marketerId, teamId) => {
    try { await adminAPI.assignRealtorToTeam(teamId, marketerId); toast.success('Reassigned'); openMembers({ id: expanded }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="animate-pulse h-32 bg-white rounded-2xl" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-neutral-600">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
        <Button onClick={openCreate} className="rounded-xl" data-testid="create-team-btn"><Plus className="h-4 w-4 mr-2" />New Team</Button>
      </div>

      {teams.map(t => (
        <Card key={t.id} className="border-neutral-border shadow-card" data-testid={`team-card-${t.id}`}>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">{t.name}{t.status === 'inactive' && <Badge variant="secondary">Inactive</Badge>}</CardTitle>
                <CardDescription>{t.description || '—'}</CardDescription>
                {t.whatsapp_link && <p className="text-xs text-green-700 mt-1 break-all">WhatsApp: {t.whatsapp_link}</p>}
                <p className="text-xs text-neutral-500 mt-1">{t.realtor_count} realtor{t.realtor_count !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => openMembers(t)} data-testid={`view-members-${t.id}`}><Users className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-team-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                {t.name !== 'General Team' && <Button variant="outline" size="sm" onClick={() => setConfirmDel(t)} className="text-red-600" data-testid={`delete-team-${t.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          </CardHeader>
          {expanded === t.id && (
            <CardContent className="border-t pt-4 space-y-4">
              <div>
                <p className="font-medium mb-2">Members</p>
                {members.length === 0 ? <p className="text-sm text-neutral-500">No realtors in this team yet.</p> : (
                  <div className="space-y-1">
                    {members.map(m => (
                      <div key={m.id} className="flex items-center justify-between p-2 rounded-lg bg-neutral-50">
                        <div>
                          <p className="text-sm font-medium">{m.full_name}</p>
                          <p className="text-xs text-neutral-500">{m.whatsapp} · {m.recruits_count || 0} recruits</p>
                        </div>
                        <select value={t.id} onChange={(e) => assign(m.id, e.target.value)} className="text-xs border rounded px-2 py-1" data-testid={`reassign-${m.id}`}>
                          {teams.map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium mb-2">Assign a realtor from another team</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                  <Input placeholder="Search realtor by name..." value={assignQuery} onChange={(e) => setAssignQuery(e.target.value)} className="pl-9" />
                </div>
                {assignQuery && (
                  <div className="mt-2 space-y-1 max-h-52 overflow-y-auto">
                    {allMarketers.filter(m => (m.full_name || '').toLowerCase().includes(assignQuery.toLowerCase()) && m.team_id !== t.id).slice(0, 20).map(m => (
                      <div key={m.id} className="flex items-center justify-between p-2 rounded-lg bg-white border">
                        <p className="text-sm">{m.full_name} <span className="text-xs text-neutral-500">({m.whatsapp})</span></p>
                        <Button size="sm" variant="outline" onClick={() => assign(m.id, t.id)} data-testid={`assign-to-${t.id}-${m.id}`}>Add</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="team-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? 'Edit Team' : 'New Team'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>WhatsApp Group Link</Label><Input value={form.whatsapp_link} onChange={(e) => setForm({ ...form, whatsapp_link: e.target.value })} placeholder="https://chat.whatsapp.com/..." data-testid="team-whatsapp-input" /></div>
            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-md h-10 px-3 text-sm">
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-team-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={confirmDel ? `Delete team "${confirmDel.name}"?` : ''}
        description="Realtors in this team will be moved to the General Team. This cannot be undone."
        confirmLabel="Delete team"
        onConfirm={performDelete}
      />
    </div>
  );
};

// =================== TRAININGS ===================
const TrainingsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', video_url: '', status: 'published' });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try { const r = await adminAPI.getTrainings(); setItems(r.data.trainings || []); }
    catch { toast.error('Failed to load trainings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit({ id: null }); setForm({ title: '', description: '', video_url: '', status: 'published' }); };
  const openEdit = (t) => { setEdit(t); setForm({ title: t.title, description: t.description || '', video_url: t.video_url, status: t.status }); };
  const save = async () => {
    if (!form.title.trim() || !form.video_url.trim()) { toast.error('Title and video URL are required'); return; }
    setSaving(true);
    try {
      if (edit?.id) await adminAPI.updateTraining(edit.id, form);
      else await adminAPI.createTraining({ ...form, display_order: items.length });
      toast.success(edit?.id ? 'Training updated' : 'Training added');
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const performDelete = async () => {
    try { await adminAPI.deleteTraining(confirmDel.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setConfirmDel(null); }
  };
  const togglePublish = async (t) => {
    try { await adminAPI.updateTraining(t.id, { status: t.status === 'published' ? 'unpublished' : 'published' }); load(); }
    catch { toast.error('Failed'); }
  };
  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await adminAPI.reorderTrainings(next.map(t => t.id)); }
    catch { load(); toast.error('Reorder failed'); }
  };

  if (loading) return <div className="animate-pulse h-32 bg-white rounded-2xl" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-neutral-600">{items.length} training{items.length !== 1 ? 's' : ''} — all realtors see the same library</p>
        <Button onClick={openCreate} className="rounded-xl" data-testid="create-training-btn"><Plus className="h-4 w-4 mr-2" />Add Training</Button>
      </div>

        {items.length === 0 && <p className="text-center py-12 text-neutral-500">No trainings yet. Click &quot;Add Training&quot; to get started.</p>}

      {items.map((t, idx) => (
        <Card key={t.id} className="border-neutral-border shadow-card" data-testid={`training-card-${t.id}`}>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="flex-shrink-0"><Video className="h-6 w-6 text-primary-600" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{t.title}</p>
                <Badge className={t.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-neutral-200'}>{t.status}</Badge>
              </div>
              {t.description && <p className="text-sm text-neutral-600 mt-1">{t.description}</p>}
              <p className="text-xs text-neutral-400 mt-1 break-all">{t.video_url}</p>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`up-${t.id}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`down-${t.id}`}><ArrowDown className="h-4 w-4" /></Button>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => togglePublish(t)} data-testid={`publish-${t.id}`}>{t.status === 'published' ? 'Unpublish' : 'Publish'}</Button>
                <Button variant="outline" size="sm" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmDel(t)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md" data-testid="training-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? 'Edit Training' : 'Add Training'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="training-title-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Video URL (YouTube/Vimeo/etc.)</Label><Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="training-url-input" /></div>
            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded-md h-10 px-3 text-sm">
                <option value="published">Published</option><option value="unpublished">Unpublished (hidden)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-training-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={confirmDel ? `Delete "${confirmDel.title}"?` : ''}
        description="This training will be permanently removed."
        confirmLabel="Delete"
        onConfirm={performDelete}
      />
    </div>
  );
};

export default AdminTeamsAndTrainings;
